import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DialogData } from 'src/app/models/dialog-data';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { BeControleFinanca } from 'src/app/models/BeControleFinanca';
import { DatePipe } from '@angular/common';
import { TokenService } from 'src/app/services/token.service';
import { BespCofiAdicionarAno } from 'src/app/models/BespCofiAdicionarAno';
import { DialogAdicionarAnoComponent } from 'src/app/components/dialog-adicionar-ano/dialog-adicionar-ano.component';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';

type ModoExibicaoControleFinancas = 'periodos' | 'movimentacoes';

@Component({
  selector: 'app-controle-financas',
  templateUrl: './list-controle-financas.component.html',
  styleUrls: ['./list-controle-financas.component.css']
})
export class ListControleFinancasComponent implements OnInit, OnDestroy {
  public dadosControleFinancas: BeControleFinanca[] = [];
  public colunasListaDados: ListaDadosColuna[] = [
    {
      campo: 'dtControle',
      rotulo: 'Controle',
      tamanho: '150px',
      formatador: (_, valor) => this.formatarPeriodo(valor),
      icone: row => row.blMesFinalizado ? 'check_circle' : undefined,
      classeIcone: () => 'lista-dados-icone--finalizado',
    },
    {
      campo: 'qtCartao',
      rotulo: 'Cartões',
      tamanho: '100px',
      formatador: (_, valor) => `(${valor ?? 0})`,
    },
    { campo: 'qtPago', rotulo: 'Pago', tamanho: '100px' },
    {
      campo: 'vlEntrada',
      rotulo: 'Entrada',
      tamanho: '150px',
      formatador: (_, valor) => this.formatarMoeda(valor),
    },
    {
      campo: 'vlSaida',
      rotulo: 'Saída',
      tamanho: '150px',
      formatador: (_, valor) => this.formatarMoeda(valor),
    },
    {
      campo: 'vlSaldo',
      rotulo: 'Saldo',
      tamanho: '150px',
      formatador: (_, valor) => this.formatarMoeda(valor),
      classeValor: (_, valor) => this.classeSaldo(valor),
      icone: (_, valor) => Number(valor) > 0 ? 'arrow_upward' : Number(valor) < 0 ? 'arrow_downward' : undefined,
    },
    {
      campo: 'vlSaldoAcumulado',
      rotulo: 'Saldo Acumulado',
      tamanho: '150px',
      formatador: (_, valor) => this.formatarMoeda(valor),
      classeValor: (_, valor) => this.classeSaldo(valor),
      icone: (_, valor) => Number(valor) > 0 ? 'arrow_upward' : Number(valor) < 0 ? 'arrow_downward' : undefined,
    },
  ];
  public subscription?: Subscription;
  public modoExibicao: ModoExibicaoControleFinancas = 'periodos';

  private readonly chaveModoExibicao = 'controle-financas.modoExibicao';

  public constructor(
    private httpClient: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private sub: SubService,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private snackBarService: SnackBarService,
    private tokenService: TokenService,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService,
  ) {}

  public ngOnInit(): void {
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao()) {
      const modoExibicao = sessionStorage.getItem(this.chaveModoExibicao);
      if (modoExibicao === 'periodos' || modoExibicao === 'movimentacoes')
        this.modoExibicao = modoExibicao;
    }
    else
      sessionStorage.setItem(this.chaveModoExibicao, 'periodos');
    this.subscription = this.httpClient.get<BeControleFinanca[]>(
      `/api/ControleFinancas`
    ).subscribe({
      next: (res) => {
        this.unsubscribe();
        this.onResult(res);
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onEdit(controleFinanca: BeControleFinanca) {
    this.router.navigate(['/controle-financa', controleFinanca.idControleFinanca], { relativeTo: this.route });
  }

  public onDelete(controleFinanca: BeControleFinanca) {
    this.showDialog('Deseja excluir esse registro?', 'Alerta', 'Não', 'Sim', controleFinanca);
  }

  public showDialog(msg: string, statusMsg: string, button: string = 'OK', secondButton: string = '', controleFinanca: BeControleFinanca = {} as BeControleFinanca) {
    if (this.subscription)
      return;
    const dialogData: DialogData = {
      title: statusMsg,
      message: msg,
      buttonText: button,
      secondButtonText: secondButton,
    };
    const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe((result) => {
      this.unsubscribe();
      if (result) {
        this.sub.reset(this.httpClient.delete(`/api/ControleFinancas/${controleFinanca.idControleFinanca}`),
          () => this.onDeleteConfirm(controleFinanca),
          () => this.showDialog('Não foi possível excluir o registro!', 'Erro')
        );
      }
    })
  }

  public onCreateYear() {
    this.dialog.open(DialogAdicionarAnoComponent, {
      data: { ano: new Date().getFullYear() }
    }).afterClosed().subscribe((ano?: number) => {
      if (ano) {
        this.apiCofiAdicionarAno(ano);
      }
    });
  }

  public onBuscarMovimentacoes() {
    this.modoExibicao = 'movimentacoes';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public exibirPeriodos() {
    this.modoExibicao = 'periodos';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public apiCofiAdicionarAno(ano: number) {
    let cpf: string | undefined;

    this.subscription = this.tokenService
      .getTokenUserCredentials()
      .subscribe((token) =>
        this.tokenService
          .decodeToken(token)
          .subscribe((authToken) => cpf = authToken!.cpf)
      );

    this.sub.reset(this.httpClient.post<BespCofiAdicionarAno>(`/api/ControleFinancas/AdicionarAno`, {
      ano,
      noCpfj: cpf
    }),
      (res) => {
        this.unsubscribe();
        if (res.retorno) {
          this.dadosControleFinancas = [...this.dadosControleFinancas, ...(res.listBeControleFinanca ?? [])]
            .sort((a, b) => new Date(b.dtControle).getTime() - new Date(a.dtControle).getTime());
          this.snackBarService.exibirSucesso(res.retornoMensagem!);
        }
        else
          this.showDialog(res.retornoMensagem!, 'Erro');
      },
      () => {
        this.showDialog('Não foi possível adicionar o ano ' + ano, 'Erro')
      }
    );
  }

  public mesAtual(dtControle: Date): boolean {
    const mesAtual = new Date(this.datePipe.transform(new Date(), 'MM-dd-yyyy') || new Date().toISOString());
    const mesDtControle = new Date(this.datePipe.transform(dtControle, 'MM-dd-yyyy') || dtControle.toISOString());
    return mesDtControle.getMonth() == mesAtual.getMonth() && mesDtControle.getFullYear() == mesAtual.getFullYear();
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private onResult(res: BeControleFinanca[]) {
    this.dadosControleFinancas = res;
  }

  private onDeleteConfirm(res: BeControleFinanca) {
    this.dadosControleFinancas = this.dadosControleFinancas.filter(item => item.idControleFinanca !== res.idControleFinanca);
    this.snackBarService.exibirSucesso('Registro excluído!');
  }

  public classeLinha(controleFinanca: BeControleFinanca): string {
    if (this.mesAtual(controleFinanca.dtControle)) {
      return 'lista-dados-card__row--mes-atual';
    }

    const periodo = this.datePipe.transform(controleFinanca.dtControle, 'yyyyMM');
    const periodoAtual = this.datePipe.transform(new Date(), 'yyyyMM');
    return periodo && periodoAtual && periodo < periodoAtual ? 'controle-financas-mes-passado' : '';
  }

  private formatarPeriodo(data: Date): string {
    const periodo = this.datePipe.transform(data, 'MMMM yyyy') ?? '';
    return periodo ? periodo.charAt(0).toUpperCase() + periodo.slice(1) : '';
  }

  private formatarMoeda(valor: number | undefined): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor ?? 0);
  }

  private classeSaldo(valor: number | undefined): string {
    return Number(valor) > 0 ? 'lista-dados-valor--saldo-positivo' : Number(valor) < 0 ? 'lista-dados-valor--saldo-negativo' : '';
  }
}
